import { keyframes } from '@emotion/react';
import { Box, Chip } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

const modalIn = keyframes`
    0% { transform: scale(.85); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
`;

const giftWiggle = keyframes`
    0%, 72%, 100% { transform: rotate(0) scale(1); }
    78% { transform: rotate(-9deg) scale(1.05); }
    84% { transform: rotate(9deg) scale(1.05); }
    90% { transform: rotate(-5deg); }
    96% { transform: rotate(4deg); }
`;

const giftPop = keyframes`
    0% { transform: scale(1); }
    40% { transform: scale(1.45) translateY(-12px); }
    100% { transform: scale(0) translateY(-46px); opacity: 0; }
`;

const couponReveal = keyframes`
    0% { transform: scale(.35) translateY(18px); opacity: 0; }
    100% { transform: scale(1) translateY(0); opacity: 1; }
`;

const confettiFall = keyframes`
    0% { transform: translate(0, 0) rotate(0); opacity: 1; }
    100% { transform: translate(var(--dx), 150px) rotate(420deg); opacity: 0; }
`;

const promoPulse = keyframes`
    0%, 100% { box-shadow: 0 12px 30px rgba(237, 137, 54, .18); }
    50% { box-shadow: 0 14px 44px rgba(237, 137, 54, .42); }
`;

const promoFlash = keyframes`
    0%, 100% { box-shadow: 0 12px 30px rgba(237, 137, 54, .18); }
    30%, 65% { box-shadow: 0 0 0 5px rgba(237, 137, 54, .5), 0 14px 44px rgba(237, 137, 54, .5); }
`;

export const BillingToggleContainer = styled(Box)`
    display: inline-flex;
    align-items: center;
    background: #f0f4ff;
    padding: 6px;
    border-radius: 50px;
    gap: 4px;
`;

export const GiftOverlay = styled('div')`
    position: fixed;
    inset: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(16, 51, 75, .5);
`;

export const GiftModal = styled('div')`
    position: relative;
    width: 100%;
    max-width: 380px;
    overflow: hidden;
    border-radius: 22px;
    background: #fff;
    padding: 42px 34px 34px;
    text-align: center;
    box-shadow: 0 24px 60px rgba(0, 0, 0, .3);
    animation: ${modalIn} .4s ease both;
`;

export const GiftCloseButton = styled('button')`
    position: absolute;
    top: 10px;
    left: 14px;
    border: 0;
    background: transparent;
    color: #aab4bd;
    cursor: pointer;
    font-size: 26px;
    line-height: 1;
`;

export const GiftButton = styled('button', {
    shouldForwardProp: prop => prop !== 'opening',
})<{ opening?: boolean }>(({ opening }) => ({
    display: 'inline-block',
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: 0,
    userSelect: 'none' as const,
    '.gift-emoji': {
        fontSize: '72px',
        lineHeight: 1,
        transformOrigin: '50% 85%',
        animation: opening ? `${giftPop} .5s ease forwards` : `${giftWiggle} 1.7s ease-in-out infinite`,
    },
}));

export const GiftHint = styled('div')`
    margin-top: 8px;
    color: #ed8936;
    font-size: 15px;
    font-weight: 700;
`;

export const GiftCoupon = styled('div')`
    position: relative;
    max-width: 330px;
    margin: 0 auto;
    border: 2px dashed #ed8936;
    border-radius: 16px;
    background: #fffaf3;
    padding: 18px 26px;
    box-shadow: 0 14px 34px rgba(237, 137, 54, .26);
    animation: ${couponReveal} .55s cubic-bezier(.2, 1.35, .5, 1) both;
`;

export const GiftCouponTop = styled('div')`
    color: #ed8936;
    font-size: 14px;
    font-weight: 700;
`;

export const GiftCouponBig = styled('div')`
    margin: 4px 0;
    color: #00843f;
    font-size: 32px;
    font-weight: 800;
    line-height: 1;
`;

export const GiftCouponSub = styled('div')`
    color: #7a8794;
    font-size: 13px;
`;

export const GiftCouponButton = styled('button')`
    display: inline-block;
    margin-top: 13px;
    border: 0;
    border-radius: 999px;
    background: #00843f;
    color: #fff;
    cursor: pointer;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    padding: 10px 24px;
    transition: background .15s;

    &:hover {
        background: #006b33;
    }
`;

export const ConfettiPieceStyled = styled('span')`
    position: absolute;
    top: 70px;
    width: 9px;
    height: 9px;
    border-radius: 2px;
    opacity: 0;
    pointer-events: none;
    animation: ${confettiFall} 1.2s ease-out forwards;
`;

export const ToggleOption = styled(Box, {
    shouldForwardProp: prop => prop !== 'isActive',
})<{ isActive?: boolean }>(({ isActive, theme }) => ({
    padding: '10px 28px',
    borderRadius: '50px',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: '1rem',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    userSelect: 'none' as const,
    ...(isActive ? {
        background: theme.palette.primary.main,
        color: '#fff',
        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.35)',
    } : {
        background: 'transparent',
        color: theme.palette.text.secondary,
        '&:hover': {
            background: alpha(theme.palette.primary.main, 0.08),
        },
    }),
}));

export const SubscriptionCard = styled(Box, {
    shouldForwardProp: prop => !['isPopular', 'isDiscounted', 'isFlashing'].includes(prop.toString()),
})<{
    isPopular?: boolean
    isDiscounted?: boolean
    isFlashing?: boolean
}>(({
    isPopular, isDiscounted, isFlashing, theme,
}) => {
    let border = '2px solid #e8e8ef';
    let boxShadow = '0 8px 30px rgba(0, 0, 0, 0.06)';
    let animation = 'none';

    if (isPopular) {
        border = `3px solid ${theme.palette.primary.main}`;
        boxShadow = '0 20px 60px rgba(25, 118, 210, 0.18)';
    }

    if (isDiscounted) {
        border = '2px solid #ed8936';
        boxShadow = '0 12px 34px rgba(237, 137, 54, .22)';
        animation = `${promoPulse} 2.4s ease-in-out infinite`;
    }

    if (isFlashing) {
        animation = `${promoFlash} 1.6s ease`;
    }

    return {
        padding: isPopular || isDiscounted ? '2.5rem 2rem' : '2rem',
        borderRadius: '20px',
        background: isPopular
            ? `linear-gradient(135deg, #ffffff 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`
            : '#ffffff',
        position: 'relative' as const,
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        border,
        boxShadow,
        animation,
        ...(isPopular && {
            transform: 'scale(1.03)',
            zIndex: 1,
        }),
        '&:hover': {
            transform: isPopular ? 'scale(1.05)' : 'translateY(-6px)',
            boxShadow: isDiscounted ? '0 14px 44px rgba(237, 137, 54, .42)' : '0 20px 60px rgba(25, 118, 210, 0.22)',
        },
    };
});

export const PopularBadge = styled(Box)`
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translateX(-50%);
    background: linear-gradient(135deg, #ff6b35 0%, #f7c948 100%);
    color: white;
    padding: 8px 28px;
    border-radius: 50px;
    font-weight: 800;
    font-size: 0.95rem;
    white-space: nowrap;
    box-shadow: 0 4px 20px rgba(255, 107, 53, 0.45);
    letter-spacing: 0.5px;
`;

export const DiscountBadge = styled(PopularBadge)`
    background: linear-gradient(90deg, #f6a23c, #ed8936);
    box-shadow: 0 4px 20px rgba(237, 137, 54, 0.45);
`;

export const DiscountBurst = styled(Box)`
    position: absolute;
    top: 16px;
    left: 16px;
    display: flex;
    width: 64px;
    height: 64px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: #ed8936;
    box-shadow: 0 4px 12px rgba(237, 137, 54, .45);
    color: #fff;
    line-height: 1;
    transform: rotate(-12deg);

    b {
        font-size: 18px;
        font-weight: 800;
    }

    small {
        margin-top: 2px;
        font-size: 10px;
        font-weight: 600;
    }
`;

export const SavingsChip = styled(Chip)`
    background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%) !important;
    color: #2e7d32 !important;
    font-weight: 700;
    font-size: 0.85rem;
    height: auto;
    padding: 4px 8px;

    .MuiChip-label {
        padding: 2px 4px;
    }
`;

export const CustomPrice = styled(Box)`
    min-height: 50px;
    margin: 22px 0 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #10334b;
    font-size: 22px;
    font-weight: 700;
    text-align: center;
`;

export const WhatsAppButton = styled('a')`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: 12px;
    border: 1.5px solid #25d366;
    border-radius: 10px;
    background: #25d366;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    padding: 12px;
    text-align: center;
    text-decoration: none;
    transition: all .15s;

    &:hover {
        border-color: #1da851;
        background: #1da851;
        color: #fff;
        text-decoration: none;
    }
`;
